<template>
    <b-row class="estimate-details">
        <b-col xl="4" md="4" sm="4">
            <b-table-simple borderless>
                <b-tbody>
                    <input
                        type="hidden"
                        name="estimate-id"
                        :value="estimate.id"
                    />
                    <b-tr class="row">
                        <b-th class="col-4">Title:</b-th>
                        <b-td class="col-8">
                            {{ estimate.title }}
                        </b-td>
                    </b-tr>
                    <b-tr class="row">
                        <b-th class="col-4">Client:</b-th>
                        <b-td class="col-8">
                            <span v-if="estimate.client">
                                <client-name
                                    :client="estimate.client"
                                    :text="estimate.client.display_name"
                                />
                            </span>
                        </b-td>
                    </b-tr>
                    <b-tr class="row">
                        <b-th class="col-4">Created:</b-th>
                        <b-td class="col-8">
                            {{ _carbonDateDisplay(estimate.start) }}
                        </b-td>
                    </b-tr>
                    <b-tr class="row" v-if="!estimate.isconvert">
                        <b-th class="col-4">Valid Until:</b-th>
                        <b-td class="col-8">
                            {{ _carbonDateDisplay(estimate.expire) }}
                        </b-td>
                    </b-tr>
                    <b-tr class="row" v-else>
                        <b-th class="col-4">Reference:</b-th>
                        <b-td class="col-8" v-if="estimate.invoice">
                            <invoice-link
                                v-if="estimate.invoice.encoded_id"
                                :invoice="estimate.invoice"
                                :newTab="false"
                            />
                        </b-td>
                        <b-td class="col-8" v-else-if="estimate.invoice_id">
                            Invoice #{{ estimate.invoice_index }} (inactive)
                        </b-td>
                        <b-td class="col-8" v-else>
                            {{ estimate.invoice }}
                        </b-td>
                    </b-tr>
                    <b-tr class="row">
                        <b-th class="col-4">Status:</b-th>
                        <b-td class="col-8">
                            {{ estimateStatus }}
                        </b-td>
                    </b-tr>
                </b-tbody>
            </b-table-simple>
        </b-col>
        <b-col xl="4" md="4" sm="4">
            <b-table-simple borderless>
                <b-tbody>
                    <b-tr class="row">
                        <b-th class="col-4">Location:</b-th>
                        <b-td class="col-8" v-if="estimate.location_id">
                            <client-name
                                :client="estimate.location"
                                :text="estimate.location.label"
                            />
                        </b-td>
                        <b-td class="col-8" v-else>
                            {{ estimate.location_name }}
                        </b-td>
                    </b-tr>
                    <b-tr class="row">
                        <b-th class="col-4">Notes: </b-th>
                        <b-td
                            class="col-8 note-spacing"
                            v-html="convertNewLineToBreak(estimate.note)"
                        >
                        </b-td>
                    </b-tr>
                    <b-tr class="row">
                        <b-th class="col-4">Medical Note:</b-th>
                        <b-td class="col-8">
                            <template v-if="estimate.active_soap.length > 0">
                                <div
                                    v-for="soap in estimate.active_soap"
                                    :key="soap.id"
                                    class="pt-2"
                                    style="line-height: 20px;"
                                >
                                    <soap-link
                                        :text="`${getPatient(soap.patient)} : ${
                                            soap.label
                                        }`"
                                        :soap="soap"
                                    />
                                </div>
                            </template>
                        </b-td>
                    </b-tr>
                    <b-tr class="row">
                        <b-th class="col-4">Signature:</b-th>
                        <b-td class="col-8" v-if="estimate.signature_img">
                            <span v-html="estimate.signature_img"></span>
                        </b-td>
                    </b-tr>
                </b-tbody>
            </b-table-simple>
        </b-col>
        <b-col xl="4" md="4" sm="4">
            <b-table-simple borderless>
                <b-tbody>
                    <b-tr class="row">
                        <b-th class="col-4">Subtotal:</b-th>
                        <b-td class="col-8">
                            {{ estimate.sub_total_amount }}
                        </b-td>
                    </b-tr>
                    <b-tr class="row">
                        <b-th class="col-4">Discount:</b-th>
                        <b-td class="col-8">
                            {{ estimate.discount_amount }}
                        </b-td>
                    </b-tr>
                    <b-tr class="row">
                        <b-th class="col-4">Tax:</b-th>
                        <b-td class="col-8">
                            {{ estimate.tax_amount }}
                        </b-td>
                    </b-tr>
                    <b-tr class="row">
                        <b-th class="col-4">Total:</b-th>
                        <b-td class="col-8">
                            {{ estimate.total_amount }}
                        </b-td>
                    </b-tr>
                </b-tbody>
            </b-table-simple>
        </b-col>
    </b-row>
</template>
<script>
/**
 * @name EstimateDisplay
 * @see apps/application/views/staff/billing/invoice/estimate/view.php
 */

import ClientName from "@/components/commons/client-name";
import InvoiceLink from "@/components/commons/invoice-link";
import PatientHelper from "@/mixins/patient";
import BILLINGS from "@/constants/billings";
import SoapLink from "@/components/commons/soap-link";
export default {
    mixins: [PatientHelper],
    components: {
        ClientName,
        InvoiceLink,
        SoapLink
    },
    props: {
        estimate: {
            type: Object
        }
    },
    data() {
        return {
            BILLINGS
        };
    },
    computed: {
        estimateStatus() {
            if (this.estimate.status_label.toLowerCase() === "approved") {
                return `${this.estimate.status_label} by ${
                    this.estimate.approver_name
                } at ${this._carbonDateDisplay(this.estimate.approve_at)}`;
            } else {
                let date = this.estimate.isconvert
                    ? this._carbonDateDisplay(this.estimate.convert_at)
                    : this.estimate.isapprove
                    ? this._carbonDateDisplay(this.estimate.approve_at)
                    : "";

                return `${this.estimate.status_label} ${
                    date ? `at ${date}` : ""
                }`;
            }
        }
    },
    methods: {
        getSoapViewLink(soap) {
            return process.env.APPS + soap.ci_url;
        },
        getPatient(patient) {
            const ownerLastName =
                patient.primary_owner &&
                Array.isArray(patient.primary_owner) &&
                patient.primary_owner.length > 0
                    ? patient.primary_owner[0].lastname
                    : "";
            const status = this.getStatus(patient);

            return `${patient.name} ${ownerLastName} ${status}`;
        }
    }
};
</script>
<style type="text/css" lang="scss" scoped>
.estimate-details th,
.estimate-details td {
    padding: 0px !important;
    line-height: 35px;
}
.estimate-details tr {
    padding-left: 1.5rem !important;
}

.fee-line {
    line-height: normal;
    padding-top: 7px;
}
.note-spacing {
    line-height: normal !important;
}
</style>
